import { DEFAULT_ATTEMPT_TIMEOUT_MS } from '../core/attempt.js';
import { formatMs } from '../core/format.js';
import type { AttemptAnomaly, Transition } from '../core/types.js';

interface Props {
  transitions: readonly Transition[];
  startedAtMs: number | null;
  result?: string;
  failureReason?: string | null;
  totalDurationMs?: number;
  tConnectedMs?: number | null;
  tOnInternetMs?: number | null;
  anomalies?: readonly AttemptAnomaly[];
  timeoutMs?: number;
}

export function StatusTimeline({
  transitions,
  startedAtMs,
  result,
  failureReason,
  totalDurationMs,
  tConnectedMs = null,
  tOnInternetMs = null,
  anomalies = [],
  timeoutMs = DEFAULT_ATTEMPT_TIMEOUT_MS,
}: Props) {
  const hasAttempt = startedAtMs !== null;

  return (
    <section className="section">
      <h2>Live Status</h2>
      {!hasAttempt && transitions.length === 0 && (
        <p className="muted">No connect attempt yet. Use the form below.</p>
      )}
      {hasAttempt && result && (
        <ResultBanner
          result={result}
          failureReason={failureReason ?? null}
          lastLabel={transitions[transitions.length - 1]?.label ?? null}
          elapsedMs={totalDurationMs ?? 0}
          tConnectedMs={tConnectedMs}
          tOnInternetMs={tOnInternetMs}
          timeoutMs={timeoutMs}
        />
      )}
      {hasAttempt && anomalies.length > 0 && <AnomalyList anomalies={anomalies} />}
      {hasAttempt && (
        <ol className="timeline">
          {transitions.map((t, i) => (
            <li key={`${i}-${t.tMs}-${t.label}`}>
              <span className="muted">{formatMs(t.tMs)}</span>
              <span>
                <span className={`label-pill label-${t.label}`}>{t.label}</span>
              </span>
              <span className="muted">raw 0x{t.raw.toString(16).padStart(2, '0')}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

interface ResultBannerProps {
  result: string;
  failureReason: string | null;
  lastLabel: string | null;
  elapsedMs: number;
  tConnectedMs: number | null;
  tOnInternetMs: number | null;
  timeoutMs: number;
}

function ResultBanner({
  result,
  failureReason,
  lastLabel,
  elapsedMs,
  tConnectedMs,
  tOnInternetMs,
  timeoutMs,
}: ResultBannerProps) {
  if (result === 'pending') {
    const elapsedS = (elapsedMs / 1000).toFixed(1);
    const timeoutS = Math.round(timeoutMs / 1000);
    const milestones = formatMilestonesInline(tConnectedMs, tOnInternetMs);
    return (
      <div className="result-banner result-banner-pending" role="status">
        <span className="result-banner-spinner" aria-hidden="true" />
        <div className="result-banner-body">
          <strong>Connecting…</strong>
          <div className="muted">
            {lastLabel ? `last status ${lastLabel}` : 'waiting for first status'}
            {' · '}
            {elapsedS} s / {timeoutS} s
            {milestones && <> · {milestones}</>}
          </div>
        </div>
      </div>
    );
  }
  const tone =
    result === 'pass' ? 'pass' : result === 'fail' ? 'fail' : 'timeout';
  const headline =
    result === 'pass'
      ? buildPassHeadline(tConnectedMs, tOnInternetMs, elapsedMs)
      : result === 'timeout'
        ? `TIMEOUT after ${formatSecs(elapsedMs)}`
        : `FAIL after ${formatSecs(elapsedMs)}`;
  return (
    <div
      className={`result-banner result-banner-${tone}`}
      role={result === 'pass' ? 'status' : 'alert'}
    >
      <span className="result-banner-icon" aria-hidden="true">
        {result === 'pass' ? '✓' : result === 'timeout' ? '⏱' : '✕'}
      </span>
      <div className="result-banner-body">
        <strong>{headline}</strong>
        {failureReason && <div className="muted">{failureReason}</div>}
        {result !== 'pass' && (
          <MilestonesLine
            tConnectedMs={tConnectedMs}
            tOnInternetMs={tOnInternetMs}
          />
        )}
      </div>
    </div>
  );
}

function MilestonesLine({
  tConnectedMs,
  tOnInternetMs,
}: {
  tConnectedMs: number | null;
  tOnInternetMs: number | null;
}) {
  const text = formatMilestonesInline(tConnectedMs, tOnInternetMs);
  if (!text) return null;
  return <div className="muted">{text}</div>;
}

function formatMilestonesInline(
  tConnectedMs: number | null,
  tOnInternetMs: number | null,
): string | null {
  const parts: string[] = [];
  if (tConnectedMs !== null) {
    parts.push(`CONNECTED at ${formatSecs(tConnectedMs)}`);
  }
  if (tOnInternetMs !== null) {
    parts.push(`ON_INTERNET at ${formatSecs(tOnInternetMs)}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function buildPassHeadline(
  tConnectedMs: number | null,
  tOnInternetMs: number | null,
  fallbackMs: number,
): string {
  const reachedAt = tOnInternetMs ?? fallbackMs;
  if (tConnectedMs !== null && tOnInternetMs !== null) {
    return `PASS — ON_INTERNET at ${formatSecs(tOnInternetMs)} (CONNECTED at ${formatSecs(tConnectedMs)})`;
  }
  return `PASS — reached ON_INTERNET in ${formatSecs(reachedAt)}`;
}

function formatSecs(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

function AnomalyList({ anomalies }: { anomalies: readonly AttemptAnomaly[] }) {
  return (
    <div className="anomaly-stack" role="alert">
      {anomalies.map((a, i) => (
        <div key={`${a.kind}-${a.tMs}-${i}`} className="anomaly-card">
          <span className="anomaly-icon" aria-hidden="true">
            ⚠
          </span>
          <div>
            <strong>Unexpected firmware behavior — {a.kind}</strong>
            <div className="muted">{a.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
